// src/pages/SiteStatusTab.tsx
// ─────────────────────────────────────────────────────────────────
// SITE STATUS TAB — Phase 2/3 implementation
// Per-job site dashboard: crew counts, push log, recall log,
// active permits, open WHS items, weather placeholder
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react'
import {
  MapPin, Users, AlertTriangle, CheckCircle, Clock,
  RefreshCw, ChevronDown, ChevronUp, Cloud, Wind,
  Activity, Bell, Shield, Package, FileText, Zap,
  Send, Archive,
} from 'lucide-react'
import { getSiteStatusSummary, getPushLogGrouped, type PushLogSession } from '../services/siteStatusService'
import { getRecallLog } from '../services/recallService'
import type { SiteStatusCard, RecallEvent } from '../types/mobileCrewTypes'
import { MOCK_JOBS } from '../utils/mockData'

// ── Helpers ────────────────────────────────────────────────────────
function timeAgo(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', {
    timeZone: 'Australia/Brisbane', hour: '2-digit', minute: '2-digit',
  })
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short',
  })
}

// ── Push Session Log ───────────────────────────────────────────────
function PushSessionLog({ sessions }: { sessions: PushLogSession[] }) {
  if (sessions.length === 0) return (
    <div className="py-8 text-center text-gray-400 text-sm">
      No push sessions recorded
    </div>
  )

  return (
    <div className="space-y-2">
      {sessions.map((s, i) => {
        const ackPct = s.shiftCount > 0 ? Math.round((s.confirmed / s.shiftCount) * 100) : 0
        return (
          <div key={s.sessionKey} className="bg-white border border-gray-100 rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                i === 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {i === 0 ? 'NOW' : fmtDate(s.pushedAt).split(' ')[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">
                    {s.shiftCount} shift{s.shiftCount !== 1 ? 's' : ''} pushed
                  </span>
                  <span className="text-xs text-gray-400">by {s.pushedByName}</span>
                  <span className="text-xs text-gray-400">·</span>
                  {s.jobNumbers.map(jn => (
                    <span key={jn} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                      {jn}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-32">
                    <div
                      className={`h-full rounded-full ${ackPct === 100 ? 'bg-green-500' : 'bg-amber-400'}`}
                      style={{ width:`${ackPct}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">
                    {s.confirmed}/{s.shiftCount} confirmed
                    {s.rejected > 0 && <span className="text-red-600 ml-1">· {s.rejected} rejected</span>}
                  </span>
                </div>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo(s.pushedAt)}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Recall Log ─────────────────────────────────────────────────────
function RecallLog({ recalls }: { recalls: RecallEvent[] }) {
  if (recalls.length === 0) return (
    <div className="py-8 text-center text-gray-400 text-sm">
      No recall events recorded
    </div>
  )

  return (
    <div className="space-y-2">
      {recalls.map(r => {
        const acked    = r.affectedCrew.filter(c => c.acknowledgedAt).length
        const returned = r.affectedCrew.filter(c => c.returnedToSiteAt).length
        const total    = r.affectedCrew.length
        return (
          <div key={r.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className={`px-4 py-3 flex items-center justify-between ${
              !r.allReturnedAt ? 'bg-red-50' : 'bg-gray-50'
            }`}>
              <div className="flex items-center gap-2">
                {!r.allReturnedAt && (
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                )}
                <span className="text-sm font-bold text-gray-900">{r.id}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  !r.allReturnedAt
                    ? 'bg-red-100 text-red-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {!r.allReturnedAt ? 'ACTIVE' : 'CLOSED'}
                </span>
              </div>
              <span className="text-xs text-gray-400">{timeAgo(r.issuedAtUTC)}</span>
            </div>
            <div className="px-4 py-3 space-y-2">
              <div className="text-sm text-gray-700">
                <strong>{r.reason}</strong>
                {r.freeText && <span className="text-gray-500"> — {r.freeText}</span>}
              </div>
              <div className="flex gap-4 text-xs text-gray-500">
                <span>Issued by {r.issuedByName}</span>
                <span>{r.urgency.replace(/_/g,' ')}</span>
                {r.musterPoint && <span>Muster: {r.musterPoint}</span>}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-40">
                  <div
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: total > 0 ? `${(acked/total)*100}%` : '0%' }}
                  />
                </div>
                <span className="text-xs text-gray-500">
                  {acked}/{total} acked · {returned}/{total} returned
                </span>
                {r.totalShutdownMinutes != null && (
                  <span className="text-xs text-blue-600 font-medium ml-auto">
                    {Math.floor(r.totalShutdownMinutes / 60)}h {r.totalShutdownMinutes % 60}m shutdown
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Site Card (per job) ────────────────────────────────────────────
function SiteCard({
  card, sessions, recalls,
}: {
  card:     SiteStatusCard
  sessions: PushLogSession[]
  recalls:  RecallEvent[]
}) {
  const [section, setSection] = useState<'overview' | 'push-log' | 'recall-log'>('overview')

  const jobSessions = sessions.filter(s => s.jobNumbers.includes(card.jobNumber))
  const jobRecalls  = recalls.filter(r => r.jobIds.includes(card.jobId))
  const latestSession = jobSessions[0]
  const ackPct = latestSession && latestSession.shiftCount > 0
    ? Math.round((latestSession.confirmed / latestSession.shiftCount) * 100) : 0

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      {/* Card header */}
      <div className={`px-5 py-4 border-b ${card.recallActive ? 'bg-red-600' : 'bg-gray-50'}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-base font-bold ${card.recallActive ? 'text-white' : 'text-gray-900'}`}>
                {card.jobNumber}
              </span>
              {card.recallActive && (
                <span className="text-xs font-bold bg-white/20 text-white px-2 py-0.5 rounded-full animate-pulse">
                  🔴 RECALL ACTIVE
                </span>
              )}
              {card.currentShift !== 'NOT_STARTED' && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  card.recallActive ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'
                }`}>
                  {(card as any).currentShift === 'DAY_ACTIVE' ? '☀️ Day shift' : '🌙 Night shift'}
                </span>
              )}
            </div>
            {card.siteAddress && (
              <div className={`flex items-center gap-1 text-xs mt-0.5 ${card.recallActive ? 'text-red-200' : 'text-gray-500'}`}>
                <MapPin className="w-3 h-3" />
                <span>{card.siteAddress}</span>
              </div>
            )}
          </div>
          {card.weather && (
            <div className={`text-right text-xs flex-shrink-0 ${card.recallActive ? 'text-red-200' : 'text-gray-500'}`}>
              <div className="font-medium text-sm">{card.weather.tempC}°C</div>
              <div className="flex items-center gap-1 justify-end">
                <Cloud className="w-3 h-3" />
                {card.weather.condition}
              </div>
              {card.weather.hasWarning && (
                <div className="text-amber-500 font-semibold mt-0.5">⚠ {card.weather.warningType}</div>
              )}
            </div>
          )}
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-4 gap-2 mt-3">
          {[
            {
              label: 'On site',
              value: card.crewOnSite,
              sub:   `of ${card.crewExpected} expected`,
              col:   card.crewOnSite >= card.crewExpected ? 'text-green-300' : 'text-amber-300',
            },
            {
              label: 'WHS items',
              value: card.activeWHSIncidents + card.activeWHSOAlerts,
              sub:   card.openPermits > 0 ? `${card.openPermits} permits` : 'all clear',
              col:   card.activeWHSIncidents > 0 ? 'text-red-300' : 'text-green-300',
            },
            {
              label: 'Last push',
              value: latestSession ? `${ackPct}%` : '—',
              sub:   latestSession ? `${latestSession.confirmed}/${latestSession.shiftCount} confirmed` : 'No push yet',
              col:   ackPct === 100 ? 'text-green-300' : ackPct > 0 ? 'text-amber-300' : 'text-gray-400',
            },
            {
              label: 'Recalls',
              value: jobRecalls.length,
              sub:   jobRecalls.filter(r => !r.allReturnedAt).length > 0 ? 'active' : 'all clear',
              col:   jobRecalls.filter(r => !r.allReturnedAt).length > 0 ? 'text-red-300' : 'text-green-300',
            },
          ].map(k => (
            <div key={k.label} className={`rounded-xl px-3 py-2 text-center ${card.recallActive ? 'bg-white/10' : 'bg-white border border-gray-100'}`}>
              <div className={`text-lg font-bold ${card.recallActive ? k.col : 'text-gray-900'}`}>{k.value}</div>
              <div className={`text-xs ${card.recallActive ? 'text-white/70' : 'text-gray-500'} font-medium`}>{k.label}</div>
              <div className={`text-xs ${card.recallActive ? 'text-white/50' : 'text-gray-400'}`}>{k.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex border-b border-gray-100">
        {([
          { key:'overview',   label:'Overview'                                    },
          { key:'push-log',   label:`Push log (${jobSessions.length})`             },
          { key:'recall-log', label:`Recalls (${jobRecalls.length})`,
            badge: jobRecalls.filter(r => !r.allReturnedAt).length },
        ] as const).map(s => (
          <button key={s.key}
            onClick={() => setSection(s.key as any)}
            className={`relative flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
              section === s.key ? 'text-blue-600 border-b-2 border-blue-600 -mb-px' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {s.label}
            {(s as any).badge != null && (s as any).badge > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs font-bold px-1 py-0 rounded-full">
                {(s as any).badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Section content */}
      <div className="p-4">
        {section === 'overview' && (
          <div className="space-y-3">
            {/* Crew by status */}
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Crew status</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label:'On site',  value:card.crewOnSite,  bg:'bg-blue-50',  text:'text-blue-700'  },
                  { label:'Pending',  value:card.crewPending,  bg:'bg-amber-50', text:'text-amber-700' },
                  { label:'Devices',  value:card.devicesOnSite,bg:'bg-gray-50',  text:'text-gray-600'  },
                ].map(s => (
                  <div key={s.label} className={`rounded-xl px-3 py-2 text-center ${s.bg}`}>
                    <div className={`text-xl font-bold ${s.text}`}>{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* WHS summary */}
            {(card.activeWHSIncidents > 0 || card.activeWHSOAlerts > 0 || card.openPermits > 0) && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">WHS & Compliance</p>
                <div className="space-y-1.5">
                  {card.activeWHSIncidents > 0 && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <span className="text-red-700 font-medium">{card.activeWHSIncidents} active WHS incident{card.activeWHSIncidents !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {card.activeWHSOAlerts > 0 && (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                      <Bell className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span className="text-amber-700 font-medium">{card.activeWHSOAlerts} active WHSO alert{card.activeWHSOAlerts !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {card.openPermits > 0 && (
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
                      <Shield className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="text-blue-700 font-medium">{card.openPermits} permit{card.openPermits !== 1 ? 's' : ''} to work active</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* All clear */}
            {card.activeWHSIncidents === 0 && card.activeWHSOAlerts === 0 && card.openPermits === 0 && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 text-sm text-green-700">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">No active WHS issues or open permits</span>
              </div>
            )}

            {/* Defective assets */}
            {card.defectiveAssetsOnSite > 0 && (
              <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-sm">
                <Zap className="w-4 h-4 text-orange-600 flex-shrink-0" />
                <span className="text-orange-700 font-medium">
                  {card.defectiveAssetsOnSite} defective asset{card.defectiveAssetsOnSite !== 1 ? 's' : ''} on site
                </span>
              </div>
            )}

            {/* Last sync */}
            {card.lastSyncAt && (
              <p className="text-xs text-gray-400 text-right">
                Last sync: {timeAgo(card.lastSyncAt)}
              </p>
            )}
          </div>
        )}

        {section === 'push-log' && (
          <PushSessionLog sessions={jobSessions} />
        )}

        {section === 'recall-log' && (
          <RecallLog recalls={jobRecalls} />
        )}
      </div>
    </div>
  )
}

// ── Main SiteStatusTab ─────────────────────────────────────────────
export function SiteStatusTab({ scopedJobIds }: { scopedJobIds: string[] | null }) {
  const [cards,    setCards]    = useState<SiteStatusCard[]>([])
  const [sessions, setSessions] = useState<PushLogSession[]>([])
  const [recalls,  setRecalls]  = useState<RecallEvent[]>([])
  const [loading,  setLoading]  = useState(true)

  const load = useCallback(async () => {
    const [c, s, r] = await Promise.all([
      getSiteStatusSummary(),
      getPushLogGrouped(),
      getRecallLog(),
    ])
    setCards(scopedJobIds ? c.filter(x => scopedJobIds.includes(x.jobId)) : c)
    setSessions(s)
    setRecalls(r)
    setLoading(false)
  }, [scopedJobIds])

  useEffect(() => { load() }, [load])

  if (loading) return (
    <div className="py-16 flex items-center justify-center text-gray-400">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" />Loading site status...
    </div>
  )

  const activeRecalls = recalls.filter(r => !r.allReturnedAt)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">
            {cards.length} active site{cards.length !== 1 ? 's' : ''}
          </h3>
          {activeRecalls.length > 0 && (
            <p className="text-sm text-red-600 font-semibold">
              ⚠ {activeRecalls.length} recall{activeRecalls.length !== 1 ? 's' : ''} currently active
            </p>
          )}
        </div>
        <button onClick={load} className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label:'Total crew on site',  value:cards.reduce((s,c)=>s+c.crewOnSite,0),           bg:'bg-blue-50',   text:'text-blue-700' },
          { label:'Active WHS issues',   value:cards.reduce((s,c)=>s+c.activeWHSIncidents,0),    bg:cards.some(c=>c.activeWHSIncidents>0)?'bg-red-50':'bg-gray-50',   text:cards.some(c=>c.activeWHSIncidents>0)?'text-red-700':'text-gray-400' },
          { label:'Active recalls',      value:activeRecalls.length,                              bg:activeRecalls.length>0?'bg-red-50':'bg-gray-50', text:activeRecalls.length>0?'text-red-700':'text-gray-400' },
          { label:'Open permits',        value:cards.reduce((s,c)=>s+c.openPermits,0),            bg:'bg-blue-50',   text:'text-blue-700' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border border-gray-200 p-3 ${s.bg}`}>
            <div className={`text-2xl font-bold ${s.text}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Per-site cards */}
      {cards.map(card => (
        <SiteCard
          key={card.jobId}
          card={card}
          sessions={sessions}
          recalls={recalls.filter(r => r.jobIds.includes(card.jobId))}
        />
      ))}

      {cards.length === 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <Activity className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">No active sites</p>
          <p className="text-gray-300 text-sm mt-1">Sites appear here once crew are pushed to mobile</p>
        </div>
      )}
    </div>
  )
}
